import { useMemo, useState } from "react";
import type { EntityType, Interaction } from "../../types";
import type { EntityInfo } from "../../hooks/useEntityMap";
import type { SegmentWithId } from "../../hooks/useSegments";
import type { EntityNode } from "../../hooks/useEntityHierarchy";
import { buildNestedSegmentOptions } from "../../utils/segmentTree";
import { isContainerEntityType } from "../../utils/activityLogging";
import { entityTypeLabel, segmentDayLookup, segmentZoneLookup } from "../../utils/tripDisplay";
import {
  CUSTOM_TYPE,
  EMPTY_FILTER,
  dayKeyOf,
  dayLabelOf,
  typeOf,
  type InteractionFilter,
} from "./interactionFilter";

interface GroupProps {
  title: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}

function FilterGroup({ title, options, selected, onToggle }: GroupProps) {
  const [open, setOpen] = useState(true);
  if (options.length === 0) return null;
  return (
    <div className="ifilter-group">
      <button type="button" className="ifilter-group-head" onClick={() => setOpen((o) => !o)}>
        <span className={`ifilter-chevron${open ? " open" : ""}`} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </span>
        {title}
        {selected.length > 0 && <span className="ifilter-group-count">{selected.length}</span>}
      </button>
      {open && (
        <div className="ifilter-chips">
          {options.map((opt) => {
            const on = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                className={`ifilter-chip${on ? " on" : ""}`}
                aria-pressed={on}
                onClick={() => onToggle(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface InteractionsFilterProps {
  interactions: (Interaction & { id: string })[];
  entityDataMap: Record<string, EntityInfo>;
  segments: SegmentWithId[];
  /** The trip's zone, so day chips are cut on the same days as the timeline. */
  tripZone?: string | null;
  /** Leaf entity id → ancestor ids (self → root), from useEntityHierarchy. */
  chains: Record<string, string[]>;
  nodes: Record<string, EntityNode>;
  /** True while the ancestor chains are still being fetched. */
  locationsLoading?: boolean;
  value: InteractionFilter;
  onChange: (next: InteractionFilter) => void;
  /** Itinerary sort direction; when provided, shows an order toggle. */
  sortDir?: "asc" | "desc";
  onToggleSort?: () => void;
}

export function InteractionsFilter({
  interactions,
  entityDataMap,
  segments,
  tripZone,
  chains,
  nodes,
  locationsLoading,
  value,
  onChange,
  sortDir,
  onToggleSort,
}: InteractionsFilterProps) {
  const [show, setShow] = useState(false);

  const options = useMemo(() => {
    const days = new Set<string>();
    const types = new Set<string>();
    const locations = new Map<string, string>(); // container entities (any depth)
    const activities = new Map<string, string>(); // the leaf entity logged
    const onDay = segmentDayLookup(segments);
    const onZone = segmentZoneLookup(segments, tripZone ?? undefined);
    for (const i of interactions) {
      const k = dayKeyOf(i, onDay, onZone);
      if (k) days.add(k);
      types.add(typeOf(i, entityDataMap));
      if (!i.entityId) continue;
      const chain = chains[i.entityId] ?? [i.entityId];
      chain.forEach((id, idx) => {
        const node = nodes[id];
        const name = node?.displayName || entityDataMap[id]?.displayName || "Unnamed";
        if (isContainerEntityType(node?.type ?? null)) {
          locations.set(id, name);
        } else if (idx === 0) {
          activities.set(id, name);
        }
      });
    }
    const sortByLabel = (a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label);
    return {
      days: [...days].sort().map((k) => ({ value: k, label: dayLabelOf(k) })),
      segments: buildNestedSegmentOptions(segments).map(({ id, label }) => ({ value: id, label })),
      types: [...types]
        .sort()
        .map((t) => ({ value: t, label: t === CUSTOM_TYPE ? "Custom" : entityTypeLabel(t as EntityType) })),
      locations: [...locations.entries()].map(([value, label]) => ({ value, label })).sort(sortByLabel),
      activities: [...activities.entries()].map(([value, label]) => ({ value, label })).sort(sortByLabel),
    };
  }, [interactions, entityDataMap, segments, tripZone, chains, nodes]);

  const toggle = (key: keyof InteractionFilter, v: string) => {
    const cur = value[key];
    onChange({ ...value, [key]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] });
  };

  const activeCount =
    value.days.length +
    value.segmentIds.length +
    value.types.length +
    value.locationIds.length +
    value.activityIds.length;

  return (
    <div className="ifilter">
      <div className="ifilter-bar">
        <button
          type="button"
          className={`ifilter-toggle${show ? " on" : ""}`}
          onClick={() => setShow((s) => !s)}
          aria-expanded={show}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filters
          {activeCount > 0 && <span className="ifilter-badge">{activeCount}</span>}
        </button>
        {activeCount > 0 && (
          <button type="button" className="ifilter-clear" onClick={() => onChange(EMPTY_FILTER)}>
            Clear
          </button>
        )}
        {sortDir && onToggleSort && (
          <button
            type="button"
            className="ifilter-sort"
            onClick={onToggleSort}
            title={sortDir === "desc" ? "Showing newest first" : "Showing oldest first"}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              {sortDir === "desc" ? (
                <polyline points="6 13 12 19 18 13" />
              ) : (
                <polyline points="6 11 12 5 18 11" />
              )}
            </svg>
            {sortDir === "desc" ? "Newest" : "Oldest"}
          </button>
        )}
      </div>

      {show && (
        <div className="ifilter-panel">
          <FilterGroup
            title="Days"
            options={options.days}
            selected={value.days}
            onToggle={(v) => toggle("days", v)}
          />
          <FilterGroup
            title="Segments"
            options={options.segments}
            selected={value.segmentIds}
            onToggle={(v) => toggle("segmentIds", v)}
          />
          <FilterGroup
            title="Types"
            options={options.types}
            selected={value.types}
            onToggle={(v) => toggle("types", v)}
          />
          {options.locations.length === 0 && locationsLoading ? (
            <div className="ifilter-group">
              <div className="ifilter-group-head">Locations</div>
              <p className="text-muted ifilter-loading">Loading…</p>
            </div>
          ) : (
            <FilterGroup
              title="Locations"
              options={options.locations}
              selected={value.locationIds}
              onToggle={(v) => toggle("locationIds", v)}
            />
          )}
          <FilterGroup
            title="Activities"
            options={options.activities}
            selected={value.activityIds}
            onToggle={(v) => toggle("activityIds", v)}
          />
        </div>
      )}
    </div>
  );
}
